import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import {
  getMinistryAreas,
  getMinistries,
  getMinistryMinistryAreas,
  createMinistryArea,
  updateMinistryArea,
  deleteMinistryArea,
  createLeader,
  deleteLeader,
  getMembers,
  getGroups,
} from '../api';
import { useAuth } from '../context/AuthContext';

export default function MinistryAreasPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ministryFromUrl = searchParams.get('ministry');
  const lowFromUrl = searchParams.get('low') === '1';
  const maleParam = searchParams.get('male');
  const femaleParam = searchParams.get('female');
  const [selectedMinistry, setSelectedMinistry] = useState(ministryFromUrl || 'all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState('');
  const [ministryAreas, setMinistryAreas] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'add',
    item: null,
    ministry_id: '',
    name: '',
    description: '',
    is_male: false,
    is_female: false,
    use_ministry_default: true,
    minimum_involvements: '',
    is_active: true,
    default_in_rotation_value: true,
    leaders: [null],
    existingLeaders: [],
  });
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [lowOnly, setLowOnly] = useState(lowFromUrl);
  const [filterMen, setFilterMen] = useState(maleParam !== null ? maleParam === '1' : true);
  const [filterWomen, setFilterWomen] = useState(femaleParam !== null ? femaleParam === '1' : true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [printLoading, setPrintLoading] = useState(false);
  const { isAdmin } = useAuth();

  const sortByDisplayOrder = (a, b) => {
    const aOrder = a.display_order ?? 999;
    const bOrder = b.display_order ?? 999;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.name || '').localeCompare(b.name || '');
  };

  const handlePrint = async () => {
    if (selectedMinistry === 'all') return;
    const ministry = ministries.find((m) => String(m.id) === selectedMinistry);
    if (!ministry) {
      setSnackbar({ open: true, message: 'Ministry not found', severity: 'error' });
      return;
    }
    setPrintLoading(true);
    try {
      const areas = await getMinistryMinistryAreas(selectedMinistry, { sortBy: 'display_order', sortOrder: 'asc' });
      const sortedAreas = [...(areas || [])].sort(sortByDisplayOrder);
      const m = { ...ministry, ministry_areas: sortedAreas };
      const getLeaderDisplayName = (l) => {
        if (!l) return null;
        if (l.group_id != null && l.group) return l.group.name;
        if (l.member) return `${l.member.first_name} ${l.member.last_name}`;
        return null;
      };
      const getLeaderInfo = (min) => {
        const names =
          min.leaders?.length > 0
            ? (min.leaders || []).map((l) => getLeaderDisplayName(l)).filter(Boolean)
            : [
                min.leader_member_id != null && min.leader && `${min.leader.first_name} ${min.leader.last_name}`,
                min.leader2_member_id != null && min.leader2 && `${min.leader2.first_name} ${min.leader2.last_name}`,
                min.leader3_member_id != null && min.leader3 && `${min.leader3.first_name} ${min.leader3.last_name}`,
              ].filter(Boolean);
        const label = names.length === 1 ? 'Ministry Leader' : 'Ministry Leaders';
        return { names, label };
      };
      const getAreaLeaderInfo = (area) => {
        const names =
          area.leaders?.length > 0
            ? (area.leaders || []).map((l) => getLeaderDisplayName(l)).filter(Boolean)
            : [];
        return names.length ? names.join(', ') : null;
      };
      const escapeHtml = (s) =>
        (s ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      const html = `<!DOCTYPE html>
<html>
<head>
  <title>Ministries</title>
  <style>
    body { font-family: sans-serif; font-size: 12px; padding: 1em; }
    .print-container { column-count: 1; }
    .ministry { break-inside: avoid; margin-bottom: 1.5em; }
    h1 { font-size: 1.5em; margin: 0 0 0.25em 0; }
    h3 { font-size: 1.1em; margin: 0.75em 0 0.25em 0; }
    .leaders { font-style: italic; margin-bottom: 0.5em; }
    .description { margin: 0 0 0.5em 0; white-space: pre-wrap; }
  </style>
</head>
<body>
  <div class="print-container">
    <div class="ministry">
      <h1>${escapeHtml(m.name)}</h1>
      <div class="leaders">${(function () {
        const { names, label } = getLeaderInfo(m);
        const leadersText = names.length ? escapeHtml(names.join(', ')) : '—';
        return `${escapeHtml(label)}: ${leadersText}`;
      })()}</div>
      ${m.description ? `<div class="description">${escapeHtml(m.description)}</div>` : ''}
      ${(m.ministry_areas || [])
        .map(
          (a) => {
            const areaLeaders = getAreaLeaderInfo(a);
            return `      <h3>${escapeHtml(a.name)}</h3>
      ${areaLeaders ? `<div class="leaders">Leaders: ${escapeHtml(areaLeaders)}</div>` : ''}
      ${a.description ? `<div class="description">${escapeHtml(a.description)}</div>` : ''}`;
          }
        )
        .join('')}
    </div>
  </div>
</body>
</html>`;
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
      win.close();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setPrintLoading(false);
    }
  };

  const loadMinistryAreas = useCallback(async () => {
    try {
      const params = { sortBy, sortOrder };
      if (search) params.search = search;
      const data =
        selectedMinistry === 'all'
          ? await getMinistryAreas(null, params)
          : await getMinistryMinistryAreas(selectedMinistry, params);
      setMinistryAreas(data);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedMinistry, sortBy, sortOrder, search]);

  const loadMinistries = async () => {
    const data = await getMinistries();
    setMinistries(data);
  };

  useEffect(() => {
    loadMinistries();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadMinistryAreas();
  }, [loadMinistryAreas]);

  const handleViewInvolvements = (m) => {
    navigate(`/admin/preferences/ministries/${m.ministry_id}/ministry-areas/${m.id}`);
  };

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortBy(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
  };

  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const { data } = await getMembers({ pageSize: 999, sortBy: 'last_name', sortOrder: 'asc' });
      setMembers(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setMembersLoading(false);
    }
  };

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const data = await getGroups({ sortBy: 'name', sortOrder: 'asc' });
      setGroups(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleAddLeaderSlot = () => {
    setDialog((d) => ({ ...d, leaders: [...d.leaders, null] }));
  };

  const handleLeaderChange = (index, value) => {
    setDialog((d) => {
      const next = [...d.leaders];
      next[index] = value; // { type: 'member', id } or { type: 'group', id } or null
      return { ...d, leaders: next };
    });
  };

  const handleOpenAdd = async () => {
    await Promise.all([loadMembers(), loadGroups()]);
    setDialog({
      open: true,
      mode: 'add',
      item: null,
      ministry_id: selectedMinistry !== 'all' ? selectedMinistry : (ministries[0]?.id ?? ''),
      name: '',
      description: '',
      is_male: false,
      is_female: false,
      use_ministry_default: true,
      minimum_involvements: '',
      is_active: true,
      default_in_rotation_value: true,
      leaders: [null],
      existingLeaders: [],
    });
  };

  const handleOpenEdit = async (m) => {
    await Promise.all([loadMembers(), loadGroups()]);
    const useDefault = m.minimum_involvements_override == null;
    const leaderEntries =
      m.leaders?.length > 0
        ? (m.leaders || []).map((l) => {
            if (l?.group_id != null || l?.group?.id != null) {
              return { type: 'group', id: l.group_id ?? l.group?.id };
            }
            if (l?.member_id != null || l?.member?.id != null) {
              return { type: 'member', id: l.member_id ?? l.member?.id };
            }
            return null;
          }).filter(Boolean)
        : [];
    const leaders = leaderEntries.length >= 1 ? leaderEntries : [null];
    setDialog({
      open: true,
      mode: 'edit',
      item: m,
      ministry_id: m.ministry_id,
      name: m.name,
      description: m.description ?? '',
      is_male: m.is_male,
      is_female: m.is_female,
      use_ministry_default: useDefault,
      minimum_involvements: useDefault ? '' : String(m.minimum_involvements_override),
      is_active: m.is_active !== false,
      default_in_rotation_value: m.default_in_rotation_value !== false,
      leaders,
      existingLeaders: m.leaders || [],
    });
  };

  const handleCloseDialog = () => setDialog({ ...dialog, open: false });

  const handleSave = async () => {
    try {
      const payload = {
        name: dialog.name,
        description: dialog.description.trim() || null,
        is_male: dialog.is_male,
        is_female: dialog.is_female,
        is_active: dialog.is_active,
        default_in_rotation_value: dialog.default_in_rotation_value,
        ...(dialog.use_ministry_default
          ? { minimum_involvements: null }
          : { minimum_involvements: parseInt(dialog.minimum_involvements, 10) }),
      };
      const ministryId = parseInt(dialog.ministry_id, 10);
      const leaderPayload = (entry) => {
        if (!entry || !entry.id) return null;
        if (entry.type === 'group') {
          return { group_id: entry.id };
        }
        return { member_id: entry.id };
      };
      if (dialog.mode === 'add') {
        const area = await createMinistryArea(ministryId, payload);
        const leaderEntries = (dialog.leaders || []).filter(Boolean);
        for (const entry of leaderEntries) {
          const lp = leaderPayload(entry);
          if (lp) {
            await createLeader({ ...lp, ministry_id: ministryId, ministry_area_id: area.id });
          }
        }
        setSnackbar({ open: true, message: 'Ministry area created', severity: 'success' });
      } else {
        const updatePayload = { ...payload, ministry_id: ministryId };
        await updateMinistryArea(dialog.item.id, updatePayload);
        const currentEntries = new Map(
          (dialog.leaders || [])
            .filter(Boolean)
            .map((e) => [`${e.type}:${e.id}`, e])
        );
        const existing = dialog.existingLeaders || [];
        for (const leader of existing) {
          const key = leader.group_id != null
            ? `group:${leader.group_id}`
            : `member:${leader.member_id ?? leader.member?.id}`;
          if (!currentEntries.has(key)) {
            await deleteLeader(leader.id);
          }
        }
        const existingKeys = new Set(
          existing.map((l) =>
            l.group_id != null ? `group:${l.group_id}` : `member:${l.member_id ?? l.member?.id}`
          )
        );
        for (const entry of currentEntries.values()) {
          const key = `${entry.type}:${entry.id}`;
          if (!existingKeys.has(key)) {
            const lp = leaderPayload(entry);
            if (lp) {
              await createLeader({ ...lp, ministry_id: ministryId, ministry_area_id: dialog.item.id });
            }
          }
        }
        setSnackbar({ open: true, message: 'Ministry area updated', severity: 'success' });
      }
      handleCloseDialog();
      loadMinistryAreas();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleDelete = (m) => setDeleteConfirm(m);

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteMinistryArea(deleteConfirm.id);
      setSnackbar({ open: true, message: 'Ministry area deleted', severity: 'success' });
      setDeleteConfirm(null);
      loadMinistryAreas();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const ministryName =
    selectedMinistry !== 'all'
      ? ministries.find((m) => String(m.id) === selectedMinistry)?.name
      : null;

  const displayedAreas = ministryAreas.filter((m) => {
    if (!filterMen && m.is_male && !m.is_female) return false;
    if (!filterWomen && m.is_female && !m.is_male) return false;
    if (lowOnly && (m.minimum_involvements == null || (m.men_count ?? 0) + (m.women_count ?? 0) >= m.minimum_involvements)) return false;
    if (statusFilter === 'enabled' && m.is_active === false) return false;
    if (statusFilter === 'disabled' && m.is_active !== false) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {ministryFromUrl && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => window.history.back()}
              size="small"
            >
              Back
            </Button>
          )}
          <Typography variant="h5" sx={{ flexShrink: 0 }}>
            Ministry Areas {ministryName ? `- ${ministryName}` : ''}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ overflow: 'visible' }}>Ministry</InputLabel>
            <Select
              value={selectedMinistry}
              label="Ministry"
              onChange={(e) => setSelectedMinistry(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {ministries.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ overflow: 'visible' }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={lowOnly}
                onChange={(e) => setLowOnly(e.target.checked)}
                size="small"
              />
            }
            label="Low Involvement Only"
            sx={{ whiteSpace: 'nowrap' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterMen}
                onChange={(e) => setFilterMen(e.target.checked)}
                size="small"
              />
            }
            label="Men"
            sx={{ whiteSpace: 'nowrap' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterWomen}
                onChange={(e) => setFilterWomen(e.target.checked)}
                size="small"
              />
            }
            label="Women"
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search ministry areas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <Tooltip title={selectedMinistry === 'all' ? 'Must select a ministry first' : 'Print ministry with descriptions and ministry areas'}>
            <span>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={selectedMinistry === 'all' || printLoading}
                sx={{ color: 'grey.600', borderColor: 'grey.400', '&:hover': { borderColor: 'grey.500', bgcolor: 'grey.100' } }}
              >
                {printLoading ? 'Loading…' : 'Print'}
              </Button>
            </span>
          </Tooltip>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              Add Ministry Area
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'ministry_id' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'ministry_id'}
                  direction={sortBy === 'ministry_id' ? sortOrder : 'asc'}
                  onClick={() => handleSort('ministry_id')}
                >
                  Ministry
                </TableSortLabel>
              </TableCell>
              <TableCell>Leaders</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>In Rotation</TableCell>
              <TableCell>Men</TableCell>
              <TableCell>Women</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Minimum</TableCell>
              <TableCell sortDirection={sortBy === 'is_male' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'is_male'}
                  direction={sortBy === 'is_male' ? sortOrder : 'asc'}
                  onClick={() => handleSort('is_male')}
                >
                  Male
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'is_female' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'is_female'}
                  direction={sortBy === 'is_female' ? sortOrder : 'asc'}
                  onClick={() => handleSort('is_female')}
                >
                  Female
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12}>Loading...</TableCell>
              </TableRow>
            ) : (
              displayedAreas.map((m) => {
                const leaderNames =
                  m.leaders?.length > 0
                    ? (m.leaders || []).map((l) => {
                        if (l?.group_id != null && l?.group) return l.group.name;
                        if (l?.member) return `${l.member.first_name} ${l.member.last_name}`;
                        return null;
                      }).filter(Boolean)
                    : [];
                return (
                <TableRow key={m.id}>
                  <TableCell>{m.name}</TableCell>
                  <TableCell>{m.ministry?.name ?? '-'}</TableCell>
                  <TableCell>{leaderNames.join(', ') || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={m.is_active !== false ? 'Enabled' : 'Disabled'}
                      size="small"
                      color={m.is_active !== false ? 'success' : 'error'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>{m.default_in_rotation_value !== false ? 'Auto added' : 'Must be approved'}</TableCell>
                  <TableCell>{m.men_count ?? 0}</TableCell>
                  <TableCell>{m.women_count ?? 0}</TableCell>
                  <TableCell>
                    {(m.men_count ?? 0) + (m.women_count ?? 0)}
                    {m.minimum_involvements != null && (m.men_count ?? 0) + (m.women_count ?? 0) < m.minimum_involvements && (
                      <Chip label="low" size="small" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </TableCell>
                  <TableCell>{m.minimum_involvements ?? '—'}</TableCell>
                  <TableCell>
                    {m.is_male ? (
                      <CheckIcon color="success" fontSize="small" />
                    ) : (
                      <CloseIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {m.is_female ? (
                      <CheckIcon color="success" fontSize="small" />
                    ) : (
                      <CloseIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewInvolvements(m)}
                      title="View Involvements"
                    >
                      <PeopleIcon />
                    </IconButton>
                    {isAdmin && (
                      <IconButton size="small" onClick={() => handleOpenEdit(m)}>
                        <EditIcon />
                      </IconButton>
                    )}
                    {isAdmin && (
                      <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ overflow: 'visible', wordBreak: 'break-word' }}>
          {dialog.mode === 'add'
            ? 'Add Ministry Area'
            : `Edit Ministry Area - ${dialog.item?.ministry?.name ?? ''}`}
        </DialogTitle>
        <DialogContent sx={{ pt: '30px !important' }}>
          {dialog.mode === 'add' && (
            <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
              <InputLabel sx={{ overflow: 'visible' }}>Ministry</InputLabel>
              <Select
                value={dialog.ministry_id}
                label="Ministry"
                onChange={(e) => setDialog({ ...dialog, ministry_id: e.target.value })}
              >
                {ministries.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            label="Name"
            value={dialog.name}
            onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
            InputLabelProps={{ sx: { overflow: 'visible' } }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Description"
            value={dialog.description}
            onChange={(e) => setDialog({ ...dialog, description: e.target.value })}
            InputLabelProps={{ sx: { overflow: 'visible' } }}
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dialog.is_active}
                onChange={(e) => setDialog({ ...dialog, is_active: e.target.checked })}
              />
            }
            label="Enabled"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dialog.default_in_rotation_value}
                onChange={(e) => setDialog({ ...dialog, default_in_rotation_value: e.target.checked })}
              />
            }
            label="By default, add member preferences to be in the rotation?"
            sx={{ mb: 2, display: 'block' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dialog.is_male}
                onChange={(e) => setDialog({ ...dialog, is_male: e.target.checked })}
              />
            }
            label="Male"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={dialog.is_female}
                onChange={(e) => setDialog({ ...dialog, is_female: e.target.checked })}
              />
            }
            label="Female"
          />
          <Box
            component="fieldset"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mt: 2,
              p: 2,
              m: 0,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <legend style={{ padding: '0 8px' }}>Minimum Involvements</legend>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dialog.use_ministry_default}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        use_ministry_default: e.target.checked,
                        minimum_involvements: e.target.checked ? '' : dialog.minimum_involvements,
                      })
                    }
                  />
                }
                label={`Use Ministry Default of ${(() => {
                  const ministry =
                    dialog.mode === 'edit'
                      ? dialog.item?.ministry
                      : ministries.find((m) => String(m.id) === dialog.ministry_id);
                  const val = ministry?.minimum_involvements;
                  return val != null ? val : '(not set)';
                })()}`}
                sx={{ flex: '1 1 50%', minWidth: 0 }}
              />
              <TextField
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Override"
                placeholder="Override"
                value={dialog.minimum_involvements}
                onChange={(e) => setDialog({ ...dialog, minimum_involvements: e.target.value })}
                disabled={dialog.use_ministry_default}
                sx={{ flex: '1 1 50%', minWidth: 0 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Minimum number of people needed for this ministry area. Uses the ministry default if the checkbox is
              checked. This affects how the Involvement Preferences form is presented—attention is drawn to ministry areas where
              involvements are below this number.
            </Typography>
          </Box>
          {(() => {
            const memberIds = new Set((members || []).map((m) => m.id));
            const extraMembers = (dialog.item?.leaders || []).map((l) => l?.member).filter((m) => m && !memberIds.has(m.id));
            const memberOptions = [...(members || []), ...extraMembers].map((m) => ({
              type: 'member',
              id: m.id,
              label: `${m.first_name} ${m.last_name}`,
            }));
            const groupIds = new Set((groups || []).map((g) => g.id));
            const extraGroups = (dialog.item?.leaders || [])
              .map((l) => l?.group)
              .filter((g) => g && !groupIds.has(g.id));
            extraGroups.forEach((g) => groupIds.add(g.id));
            const groupOptions = [
              ...(groups || []),
              ...extraGroups,
            ].map((g) => ({
              type: 'group',
              id: g.id,
              label: `${g.name} (Group)`,
            }));
            const options = [...memberOptions, ...groupOptions];
            const leaders = dialog.leaders || (dialog.mode === 'add' ? [null] : []);
            const findOption = (entry) =>
              entry
                ? options.find((o) => o.type === entry.type && o.id === entry.id)
                : null;
            return (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Leaders
                </Typography>
                {leaders.map((entry, index) => (
                  <Autocomplete
                    key={index}
                    sx={{ mt: index === 0 ? 0 : 2 }}
                    options={options}
                    getOptionLabel={(opt) => (opt ? opt.label : '')}
                    value={findOption(entry) || null}
                    onChange={(_, v) => handleLeaderChange(index, v ? { type: v.type, id: v.id } : null)}
                    loading={membersLoading || groupsLoading}
                    renderInput={(params) => (
                      <TextField {...params} label={`Leader ${index + 1}`} placeholder="Select member or group" />
                    )}
                  />
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddLeaderSlot}
                  sx={{ mt: 2 }}
                  size="small"
                >
                  Add leader
                </Button>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              !dialog.name.trim() ||
              (dialog.mode === 'add' && !dialog.ministry_id) ||
              (!dialog.use_ministry_default &&
                (dialog.minimum_involvements === '' ||
                  isNaN(parseInt(dialog.minimum_involvements, 10)) ||
                  parseInt(dialog.minimum_involvements, 10) < 0))
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Ministry Area</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteConfirm?.name}"?
          {deleteConfirm && (() => {
            const n = (deleteConfirm.men_count ?? 0) + (deleteConfirm.women_count ?? 0);
            return (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This area has {n} member{n !== 1 ? 's' : ''} with involvement records.
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
